// Stages: one item (a picture, a line, a character) built in a few steps. This is the page they all share: the top bar,
// the stage bar under it, the body, and a footer that only moves on to the next stage. See docs/ui-patterns.md, pattern B.
// The stage bar has an icon-only Overview button, then a button for each stage. One is the current one (ink), done
// (a tick), working (a spinner: something is being made there), open, or waiting (dashed, with a lock). Any chip can be
// tapped at any time, even while something is being made. Tapping a waiting one does not open it: its reason shows as a
// note on the page instead, with the button that puts it right if there is one.
// The caller owns which stage is showing (`at`) and draws its content as the children; the Overview is a stage like the
// others as far as the shell goes; its footer is just Done.
// A stage can be optional (an image's Fix faces and Animate): the flow never pushes on to it. Once no required stage is
// left, the footer's main button is Done, which closes the page, with a small button under it for each optional stage
// you are not on.
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { Body, DoneFooter, Footer, Icon, Layer, Page, Toast, TopBar } from "../common";
import "./stages.css";

// One stage. `wait` is why it can't be opened yet, and a button that fixes that if there is one.
export type Stage<K extends string = string> = {
  key: K; label: string; // "Picture"
  done?: boolean; working?: boolean;
  wait?: { reason: ReactNode; action?: ReactNode };
  optional?: { button: string; icon: string }; // its small footer button: "Fix faces", with the face icon
};
export type StageAt<K extends string = string> = "overview" | K;

// Opening a stage from inside the page (an Overview row): the same as tapping its chip, so a waiting one says why.
// A page whose own inline button is the next useful thing (Paint) calls this so the footer button turns plain white.
export const FootPlainContext = createContext<(plain: boolean) => void>(() => {});
export function useFootPlain(plain: boolean) {
  const set = useContext(FootPlainContext);
  useEffect(() => { set(plain); return () => set(false); }, [plain, set]);
}
const GoContext = createContext<(to: string) => void>(() => {});
export const useStageGo = <K extends string>() => useContext(GoContext) as (to: StageAt<K>) => void;

export function StagePage<K extends string>({ title, sub, onBack, stages, at, onAt, onDone, className, toast, clearToast, hideBar, children }: {
  title: ReactNode; sub?: ReactNode; onBack: () => void;
  stages: Stage<K>[]; at: StageAt<K>; onAt: (at: StageAt<K>) => void;
  onDone: () => void; // "Done" on the last stage: closes the page
  className?: string; toast?: string | null; clearToast?: () => void;
  hideBar?: boolean; // an item with a single stage (a place, a prop) has no stage bar
  children: ReactNode;
}) {
  const [footPlain, setFootPlain] = useState(false);
  const [asked, setAsked] = useState<K | null>(null); // a waiting stage that was tapped
  const here = stages.find(s => s.key === at);
  const next = here ? stages.slice(stages.indexOf(here) + 1).find(s => !s.optional) : undefined; // the next required stage
  const extras = stages.filter(s => s.optional && s.key !== at);
  const waiting = (stages.find(s => s.key === asked) ?? here)?.wait; // the tapped waiting stage's reason, else this one's

  // Opens a stage, or, if it has to wait, shows why.
  const go = (to: StageAt<K>) => {
    const s = stages.find(x => x.key === to);
    if (s?.wait) return setAsked(s.key);
    setAsked(null);
    onAt(to);
  };

  return (
    <Layer>
      <Page className={`tl-page${className ? ` ${className}` : ""}`}>
        <TopBar onBack={onBack} title={title} sub={sub} />
        {!hideBar && <StageBar stages={stages} at={at} onAt={go} />}
        <Body page={at}>
          <FootPlainContext.Provider value={setFootPlain}><GoContext.Provider value={go as (to: string) => void}>
            {waiting && <div className="stg-note" role="status"><p>{waiting.reason}</p>{waiting.action}</div>}
            {!here?.wait && children}
          </GoContext.Provider></FootPlainContext.Provider>
        </Body>
        {!here && <DoneFooter onDone={onDone} />}
        {here && (
          <Footer>
            <div className="stg-foot">
              {next
                ? <button className={`btn big wide ${footPlain ? "plain" : "yellow"}${next.wait ? " dim" : ""}`} onClick={() => go(next.key)}>Next: {next.label} <Icon name="chevron" /></button>
                : <button className={`btn big wide ${footPlain ? "plain" : "yellow"}`} onClick={onDone}><Icon name="check" /> Done</button>}
              {!next && extras.length > 0 && (
                <div className="stg-more">
                  {extras.map(s => (
                    <button key={s.key} className={`btn small soft${s.wait ? " dim" : ""}`} onClick={() => go(s.key)}><Icon name={s.optional!.icon} size={16} /> {s.optional!.button}</button>
                  ))}
                </div>
              )}
            </div>
          </Footer>
        )}
        {toast && <Toast onClick={clearToast}>{toast}</Toast>}
      </Page>
    </Layer>
  );
}

// The chips under the top bar.
export function StageBar<K extends string>({ stages, at, onAt }: { stages: Stage<K>[]; at: StageAt<K>; onAt: (at: StageAt<K>) => void }) {
  return (
    <nav className="stg-bar" aria-label="Stages">
      <button className={`stg-chip ov${at === "overview" ? " on" : ""}`} aria-label="Overview" aria-current={at === "overview" ? "step" : undefined} onClick={() => onAt("overview")}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" aria-hidden="true"><rect x="4" y="4" width="7" height="7" rx="1.5" /><rect x="13" y="4" width="7" height="7" rx="1.5" /><rect x="4" y="13" width="7" height="7" rx="1.5" /><rect x="13" y="13" width="7" height="7" rx="1.5" /></svg>
      </button>
      {stages.map(s => (
        <button key={s.key} className={`stg-chip${at === s.key ? " on" : ""}${s.done ? " done" : ""}${s.wait ? " lock" : ""}${s.label.length > 9 ? " long" : ""}`}
          aria-current={at === s.key ? "step" : undefined} aria-disabled={s.wait ? true : undefined} onClick={() => onAt(s.key)}>
          {s.wait ? <Icon name="lock" size={13} /> : s.working ? <span className="spinner" role="status" aria-label="Working" /> : s.done ? <Icon name="check" size={14} /> : null}
          {s.label}
        </button>
      ))}
    </nav>
  );
}

// The Overview's small preview: a frame that scrolls away with the page. The caller draws what is in it.
export function OverviewPeek({ children }: { children: ReactNode }) {
  return <div className="peek stg-peek"><div className="frame-box peek-frame">{children}</div></div>;
}

// The Overview's rows, one per stage, in a card.
export function OverviewRows({ children }: { children: ReactNode }) {
  return <div className="stg-rows">{children}</div>;
}

// One row: a thumbnail, "1 Picture", what the stage holds now, and a chevron. Tapping opens the stage.
// `thumb` is a small picture, or (with `round`) a person's face.
export function OverviewRow({ thumb, round, title, sub, waiting, onClick }: { thumb: ReactNode; round?: boolean; title: ReactNode; sub: ReactNode; waiting?: boolean; onClick: () => void }) {
  return (
    <button className={`stg-row${waiting ? " waiting" : ""}`} onClick={onClick}>
      <span className={`stg-thumb${round ? " round" : ""}`}>{thumb}</span>
      <span className="stg-row-text"><b>{title}</b><span>{sub}</span></span>
      {waiting ? <Icon name="lock" size={16} /> : <Icon name="chevron" size={18} />}
    </button>
  );
}

// The item's own actions at the foot of its Overview ("This picture": Delete). Every button acts at once.
export function ItemActions({ title, children }: { title: ReactNode; children: ReactNode }) {
  return <section className="stg-actsec"><h2>{title}</h2><div className="stg-acts">{children}</div></section>;
}
